import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MatchRow } from "../../components/MatchRow.js";
import { MatchTools } from "../../components/MatchTools.js";
import { Notice } from "../../components/ui.js";
import { useAuth } from "../../lib/auth.js";
import { fetchMatches } from "../../lib/matches.js";
import type { TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen, useDraw } from "./shared.js";

/**
 * Every score of the event, newest first.
 *
 * These are the **same rows as the match log**, rendered by the same component, because they
 * are the same matches: `score_fixture` writes into `matches` like everything else, and an
 * event result that looked different from a casual one would be implying a difference that
 * does not exist. Faces, the scoreline from the winner's side, who is waiting to confirm —
 * all of it comes for free by not building a second list.
 *
 * The event's name is suppressed on the row. It is the title of the screen.
 */
export function EventResults() {
  return <EventGate>{({ event }) => <Results event={event} />}</EventGate>;
}

function Results({ event }: { event: TournamentEvent }) {
  const { member } = useAuth();
  const queryClient = useQueryClient();

  // The draw, only to say "3 of 8" — the results themselves come from the match log.
  const { data: fixtures = [] } = useDraw(event.id);

  const {
    data: page,
    isPending,
    error,
  } = useQuery({
    queryKey: ["event-results", event.id],
    queryFn: () => fetchMatches({ eventId: event.id }, { limit: 100 }),
  });

  const matches = page?.matches ?? [];

  /**
   * Anything acted on here changes four things: this list, the standings underneath it, the
   * draw's played count, and the queue in the tab bar. The screen an organiser has open on
   * the night is the one where a confirmation is most likely to happen, so all four are
   * re-read rather than left to go stale until somebody navigates.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["event-results", event.id] });
    void queryClient.invalidateQueries({ queryKey: ["draw", event.id] });
    void queryClient.invalidateQueries({ queryKey: ["my-matches"] });
    void queryClient.invalidateQueries({ queryKey: ["match-feed"] });
  };

  const pending = matches.filter((match) => match.status === "pending").length;

  return (
    <EventScreen event={event} title="Results">
      {error ? <Notice tone="attention">Couldn&rsquo;t load the results. Try again.</Notice> : null}

      {matches.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {isPending ? "Loading…" : "Nothing has been played yet."}
        </p>
      ) : (
        <>
          <p className="micro mb-3">
            {matches.length} of {fixtures.length || matches.length} played
            {/* Said at the top, because a tournament's scores are entered in a burst and the
                ones still waiting are the reason the standings look wrong. */}
            {pending > 0 ? ` · ${pending} awaiting confirmation` : ""}
          </p>

          {matches.map((match) => (
            <MatchRow key={match.id} match={match} memberId={member?.id} showEvent={false}>
              <MatchTools match={match} onDone={refresh} />
            </MatchRow>
          ))}

          <Link to={`/events/${event.id}/standings`} className="micro mt-6 inline-block underline">
            Standings
          </Link>
        </>
      )}
    </EventScreen>
  );
}
